import { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { Form } from "react-bootstrap";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import LoadingOverlay from "react-loading-overlay-ts";
import SummaryView from "../SummaryView/SummaryView";
import CreateViewDialog from "../../CreateViewDialog";
import ViewHeader from "./ViewHeader";
import MaximizedView from "./MaximizedView";
import ProjectGrid from "../../ModuleGrids/ProjectGrid";
import TaskGrid from "../../ModuleGrids/TaskGrid";
import JobsGrid from "../../ModuleGrids/JobsGrid";
import AlertsGrid from "../../ModuleGrids/AlertsGrid";
import HelpNeededGrid from "../../ModuleGrids/HelpNeededGrid";
import FullkitGrid from "../../ModuleGrids/FullkitGrid";
import ConfirmationDialogBox from "../../../ConfirmDialog/ConfirmationDialogBox";
import {
  getDashboardModuleDetails,
  applyDashboardTemplate,
  createOrUpdateDashboardModuleView,
} from "../../../../apis/dashboardApi";
import {
  VIEW_COLORS,
  SUCCESS_TOAST_LIFE,
  ERROR_TOAST_LIFE,
  INFO_TOAST_LIFE,
  ACCORDION_TOGGLE_MIN_ITEMS,
  SUMMARY_ACCESS_ROLES,
} from "../../../../constants/dashboardConstants";
import {
  buildDashboardModule,
  buildDashboardPayload,
} from "../../CreateViewDialog/dashboardPayloadConstants";
import "./DashboardTab.scss";

const DashboardTab = () => {
  const toast = useRef(null);
  const [selectedView, setSelectedView] = useState("");
  const [selectedDefault, setSelectedDefault] = useState("");
  const [dialogVisible, setDialogVisible] = useState(false);
  const [showTemplateConfirmation, setShowTemplateConfirmation] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [maximizedItem, setMaximizedItem] = useState(null);
  const [maximizedData, setMaximizedData] = useState([]);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [currentSettings, setCurrentSettings] = useState(null);
  const [viewItems, setViewItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSummaryView, setShowSummaryView] = useState(false);
  const [summaryModuleData, setSummaryModuleData] = useState({ counts: null, moduleId: null });

  const { userInformation } = useSelector((state) => state.UserReducer);
  const {
    userDetails: { AccessGroup },
  } = useSelector((state) => state?.UserDetailsReducer);
  const roleFormat = () => {
    return AccessGroup?.map((role) => {
      return role?.AccessGroupNames?.substr(4);
    });
  };
  const userDetailsInformation = roleFormat();
  const isSummaryAccess = userDetailsInformation?.some((role) =>
    SUMMARY_ACCESS_ROLES?.includes(role),
  );

  // Fetch dashboard modules on component mount
  useEffect(() => {
    if (userInformation?.userid) {
      fetchDashboardViewsData();
    }
  }, [userInformation?.userid]);

  const fetchDashboardViewsData = async (skipMaxRefresh = false) => {
    setLoading(true);
    try {
      // Use Min mode for summary/minimized data
      const response = await getDashboardModuleDetails(userInformation?.userid, "", "Min");
      const dashboardModules = response?.Dashboard?.DashboardModuleList || [];
      if (dashboardModules?.length > 0) {
        // Extract Summary module data if it exists
        const summaryModule = dashboardModules.find(
          (module) => module?.Module_Type?.toLowerCase() === "summary",
        );

        if (summaryModule) {
          // Extract summary counts data and module ID
          setSummaryModuleData({
            counts: summaryModule?.SummaryCount || {},
            moduleId: summaryModule?.Module_ID || null,
          });
          setShowSummaryView(true);
        } else {
          setSummaryModuleData({ counts: {}, moduleId: null });
          setShowSummaryView(false);
        }

        // Parse non-summary modules for regular display
        const nonSummaryModules = dashboardModules.filter(
          (module) => module?.Module_Type?.toLowerCase() !== "summary",
        );

        const parsedViews = nonSummaryModules.map((module, index) => {
          let type = module?.Module_Type?.toLowerCase();
          // Map type to the corresponding property name
          const typeToListKey = {
            task: "TaskList",
            project: "ProjectList",
            jobs: "JobsList",
            fullkit: "FullkitList",
            alerts: "AlertsList",
            helpneeded: "HelpneededList",
          };
          const listKey = typeToListKey[type];
          const data = listKey && Array.isArray(module?.[listKey]) ? module?.[listKey] : [];
          // Assign color based on type
          let color = VIEW_COLORS[type] || VIEW_COLORS.default;
          return {
            id: index,
            title: module?.Module_Name,
            type,
            color,
            settings: module?.Config || {},
            data,
            moduleId: module?.Module_ID,
            mode: module?.Mode,
            Sequence: module?.Sequence
          };
        });
        setViewItems(parsedViews);

        // Refresh maximized view data if one is currently displayed and not skipped
        if (!skipMaxRefresh && maximizedItem?.moduleId) {
          try {
            const maxResponse = await getDashboardModuleDetails(
              userInformation?.userid,
              maximizedItem?.moduleId,
              "Max",
            );
            const maxDashboardModules = maxResponse?.Dashboard?.DashboardModuleList || [];
            if (maxDashboardModules?.length > 0) {
              const typeCapitalized =
                maximizedItem?.type?.charAt(0)?.toUpperCase?.() + maximizedItem?.type?.slice?.(1);
              const listKey = `${typeCapitalized}List`;
              const updatedData = maxDashboardModules[0]?.[listKey] || [];
              setMaximizedData(updatedData);
            }
          } catch (maxError) {
            console.error("Error refreshing maximized view data:", maxError);
          }
        }

        toast.current?.show?.({
          severity: "success",
          summary: "Success",
          detail: "Dashboard modules loaded successfully",
          life: SUCCESS_TOAST_LIFE,
        });
      } else {
        setViewItems([]);
        setSummaryModuleData({ counts: {}, moduleId: null });
        setShowSummaryView(false);
        toast.current?.show?.({
          severity: "info",
          summary: "No Modules",
          detail:
            'No Dashboard modules found. To get started, add a module by selecting an option from the "Add to view" dropdown or apply a default template using the "Apply Default" option.',
          life: INFO_TOAST_LIFE,
        });
      }
    } catch (error) {
      console.error("Error fetching dashboard modules:", error);
      toast.current?.show?.({
        severity: "error",
        summary: "Error",
        detail: "Failed to load dashboard modules.",
        life: ERROR_TOAST_LIFE,
      });
      setViewItems([]);
      setSummaryModuleData({ counts: {}, moduleId: null });
      setShowSummaryView(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async (skipMaxRefresh = false) => {
    // Reset maximized view on refresh
    setMaximizedItem(null);
    setMaximizedData([]);
    if (userInformation?.userid) {
      await fetchDashboardViewsData(skipMaxRefresh);
    } else {
      toast.current?.show?.({
        severity: "warn",
        summary: "Warning",
        detail: "User information not available",
        life: SUCCESS_TOAST_LIFE,
      });
    }
  };

  // Filter out summary module first, then split by id
  const nonSummaryItems = useMemo(() => {
    return viewItems?.filter?.((item) => !item?.isSummaryView) ?? [];
  }, [viewItems]);

  // Determine if we should reverse column distribution based on summary presence
  const shouldReverseColumns = showSummaryView;

  // Left column: even-indexed by default, odd-indexed if summary present
  const leftColumnItems = useMemo(
    () =>
      nonSummaryItems?.filter?.((item, index) =>
        shouldReverseColumns ? index % 2 !== 0 : index % 2 === 0,
      ) ?? [],
    [nonSummaryItems, shouldReverseColumns],
  );

  // Right column: odd-indexed by default, even-indexed if summary present
  const rightColumnItems = useMemo(
    () =>
      nonSummaryItems?.filter?.((item, index) =>
        shouldReverseColumns ? index % 2 === 0 : index % 2 !== 0,
      ) ?? [],
    [nonSummaryItems, shouldReverseColumns],
  );

  const projectCount = nonSummaryItems?.filter?.((item) => item?.type === "project")?.length ?? 0;
  const taskCount = nonSummaryItems?.filter?.((item) => item?.type === "task")?.length ?? 0;
  const jobCount = nonSummaryItems?.filter?.((item) => item?.type === "jobs")?.length ?? 0;
  const fullkitCount = nonSummaryItems?.filter?.((item) => item?.type === "fullkit")?.length ?? 0;
  const alertsCount = nonSummaryItems?.filter?.((item) => item?.type === "alerts")?.length ?? 0;
  const helpNeededCount =
    nonSummaryItems?.filter?.((item) => item?.type === "helpneeded")?.length ?? 0;
  const countsArray = [
    { projectCount },
    { taskCount },
    { jobCount },
    { fullkitCount },
    { alertsCount },
    { helpNeededCount },
  ];
  // Active index state for accordions, set after API loads
  const [activeIndexLeft, setActiveIndexLeft] = useState([]);
  const [activeIndexRight, setActiveIndexRight] = useState([]);
  const [allExpanded, setAllExpanded] = useState(true);

  useEffect(() => {
    if (allExpanded) {
      setActiveIndexLeft(leftColumnItems?.length ? leftColumnItems?.map?.((_, idx) => idx) : []);
      setActiveIndexRight(rightColumnItems?.length ? rightColumnItems?.map?.((_, idx) => idx) : []);
    } else {
      setActiveIndexLeft([]);
      setActiveIndexRight([]);
    }
  }, [leftColumnItems, rightColumnItems, allExpanded]);

  const handleToggleAccordion = () => {
    setAllExpanded((prev) => !prev);
  };

  const handleViewChange = (e) => {
    const value = e.target.value;
    if (value === "summary") {
      handleAddSummaryModule();
    } else if (value) {
      setSelectedView(value);
      setDialogVisible(true);
    }
  };

  const handleAddSummaryModule = async () => {
    setLoading(true);
    try {
      // Build the Summary module payload using the constants and helpers
      const summaryModule = buildDashboardModule({
        moduleId: "",
        moduleName: "Summary",
        moduleType: "Summary",
        userId: userInformation?.userid,
        config: {},
      });

      // Build the full dashboard payload
      const payload = buildDashboardPayload({
        module: summaryModule,
      });

      // Call the API to create/update the summary module
      await createOrUpdateDashboardModuleView(payload);

      toast.current?.show?.({
        severity: "success",
        summary: "Success",
        detail: "Summary module added successfully",
        life: SUCCESS_TOAST_LIFE,
      });

      // Reset the dropdown
      setSelectedView("");

      // Refresh the dashboard to show the newly added summary
      await fetchDashboardViewsData();
    } catch (error) {
      console.error("Error adding summary module:", error);
      toast.current?.show?.({
        severity: "error",
        summary: "Error",
        detail: "Failed to add summary module. Please try again.",
        life: ERROR_TOAST_LIFE,
      });
      setSelectedView("");
    } finally {
      setLoading(false);
    }
  };

  const handleDefaultChange = (e) => {
    const value = e.target.value;

    if (value) {
      // Check if there are existing modules on the dashboard
      const nonSummaryModules = viewItems.filter((item) => !item.isSummaryView);

      if (nonSummaryModules.length > 0 || showSummaryView) {
        // Show confirmation dialog
        setPendingTemplate(value);
        setShowTemplateConfirmation(true);
      } else {
        // No existing modules, apply template directly
        applyTemplate(value);
      }
    }
  };

  const applyTemplate = async (templateValue) => {
    setLoading(true);
    try {
      await applyDashboardTemplate(userInformation.userid, templateValue);
      setSelectedDefault("");
      setSummaryModuleData({ counts: {}, moduleId: null });
      setShowSummaryView(false);
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: `Dashboard template "${templateValue}" applied successfully`,
        life: SUCCESS_TOAST_LIFE,
      });
      // Refresh the entire dashboard
      await fetchDashboardViewsData();
    } catch (error) {
      console.error("Error applying template:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to apply dashboard template. Please try again.",
        life: ERROR_TOAST_LIFE,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmTemplate = async () => {
    // Close the confirmation dialog
    setShowTemplateConfirmation(false);
    if (pendingTemplate) {
      await applyTemplate(pendingTemplate);
    }
    setPendingTemplate(null);
  };

  const handleCancelTemplate = () => {
    setShowTemplateConfirmation(false);
    setPendingTemplate(null);
    setSelectedDefault("");
  };

  const handleDialogHide = () => {
    setDialogVisible(false);
    setSelectedView("");
    setIsUpdateMode(false);
    setCurrentSettings(null);
  };

  const handleDeleteSummaryView = async () => {
    setShowSummaryView(false);
    setSummaryModuleData({ counts: {}, moduleId: null });
  };

  const handleMaximize = (itemId) => {
    const item = viewItems?.find?.((item) => item?.id === itemId);
    if (item) {
      setLoading(true);
      (async () => {
        try {
          const response = await getDashboardModuleDetails(
            userInformation?.userid,
            item?.moduleId,
            "Max",
          );
          const dashboardModules = response?.Dashboard?.DashboardModuleList || [];
          if (dashboardModules?.length > 0) {
            const typeCapitalized =
              item?.type?.charAt?.(0)?.toUpperCase?.() + item?.type?.slice?.(1);
            const listKey = `${typeCapitalized}List`;
            const data = dashboardModules?.[0]?.[listKey] || [];
            setMaximizedData(data);
          }
          setMaximizedItem({
            ...item,
            allModuleNames: nonSummaryItems?.map?.((mod) => ({
              moduleId: mod?.moduleId,
              moduleName: mod?.title,
            })),
          });
        } catch (error) {
          toast.current?.show?.({
            severity: "error",
            summary: "Error",
            detail: "Failed to load maximized view data.",
            life: 5000,
          });
        } finally {
          setLoading(false);
        }
      })();
    }
  };

  const handleMinimize = () => {
    setMaximizedItem(null);
    setMaximizedData([]);
  };

  const handleDeleteSuccess = (itemId = null) => {
    setLoading(true)
    setMaximizedItem(null);
    setMaximizedData([]);
    
    // updating UI untill further API call
    setViewItems(existingItems => existingItems.filter(item => item?.id !== itemId))
    setLoading(false)
  };

  const handleSettings = (itemId) => {
    const item = viewItems?.find?.((item) => item?.id === itemId);
    if (item && !item?.isSummaryView) {
      setSelectedView(item?.type);
      setCurrentSettings({ ...item?.settings, Module_ID: item?.moduleId, Title: item?.title, Sequence: item?.Sequence });
      setIsUpdateMode(true);
      setDialogVisible(true);
    }
  };

  const handleTitleSave = (itemId, newTitle) => {
    // Update the title in viewItems
    setViewItems((prevItems) =>
      prevItems?.map?.((item) => (item?.id === itemId ? { ...item, title: newTitle } : item)),
    );

    // If the maximized item is the one being updated, update its title too
    if (maximizedItem && maximizedItem?.id === itemId) {
      setMaximizedItem((prevItem) => ({
        ...prevItem,
        title: newTitle,
      }));
    }
  };

  // Reusable column component
  const DashboardColumn = ({
    items,
    activeIndex,
    toast,
    handleMaximize,
    handleSettings,
    handleRefresh,
    showSummary,
    summaryData,
  }) => (
    <div className="view-column">
      {showSummary && items === leftColumnItems && isSummaryAccess && (
        <SummaryView
          onDelete={handleDeleteSummaryView}
          summaryData={summaryModuleData}
          toast={toast}
        />
      )}
      <Accordion multiple className="view-accordion" activeIndex={activeIndex}>
        {items?.map?.((item) => (
          <AccordionTab
            key={item?.id}
            header={
              <ViewHeader
                item={{
                  ...item,
                  allModuleNames: nonSummaryItems?.map?.((mod) => ({
                    moduleId: mod?.moduleId,
                    moduleName: mod?.title,
                  })),
                }}
                toast={toast}
                onMaximize={() => handleMaximize(item?.id)}
                onSettings={() => handleSettings(item?.id)}
                onDeleteSuccess={() => handleDeleteSuccess(item?.id)}
                onTitleSave={(newTitle) => handleTitleSave(item?.id, newTitle)}
              />
            }
            className="view-item"
            style={{ backgroundColor: item?.color }}
          >
            <div className="view-item-content">
              {item?.type === "project" ? (
                <ProjectGrid
                  data={item?.data}
                  mode="min"
                  dataKey={item?.moduleId}
                  settings={item?.settings}
                  title={item?.title}
                />
              ) : item?.type === "task" ? (
                <TaskGrid
                  data={item?.data}
                  mode="min"
                  dataKey={item?.moduleId}
                  settings={item?.settings}
                  title={item?.title}
                />
              ) : item?.type === "fullkit" ? (
                <FullkitGrid
                  data={item?.data}
                  mode="min"
                  dataKey={item?.moduleId}
                  settings={item?.settings}
                  title={item?.title}
                />
              ) : item?.type === "jobs" ? (
                <JobsGrid
                  data={item?.data}
                  mode="min"
                  dataKey={item?.moduleId}
                  settings={item?.settings}
                  title={item?.title}
                />
              ) : item?.type === "alerts" ? (
                <AlertsGrid
                  data={item?.data}
                  mode="min"
                  dataKey={item?.moduleId}
                  settings={item?.settings}
                  title={item?.title}
                />
              ) : item?.type === "helpneeded" ? (
                <HelpNeededGrid
                  data={item?.data}
                  mode="min"
                  dataKey={item?.moduleId}
                  settings={item?.settings}
                  title={item?.title}
                />
              ) : (
                <div style={item?.height ? { minHeight: `${item?.height}px` } : {}}>
                  <p>
                    Content for {item?.title} {item?.type}
                  </p>
                </div>
              )}
            </div>
          </AccordionTab>
        ))}
      </Accordion>
    </div>
  );
  return (
    <LoadingOverlay active={loading} spinner text="Loading...">
      <Toast ref={toast} />
      <CreateViewDialog
        visible={dialogVisible}
        selectedOption={selectedView}
        onHide={handleDialogHide}
        isUpdateMode={isUpdateMode}
        settings={currentSettings}
        counts={countsArray}
        reloadDashboard={fetchDashboardViewsData}
        toastRef={toast}
      />
      <ConfirmationDialogBox
        visible={showTemplateConfirmation}
        onHide={() => setShowTemplateConfirmation(false)}
        message={
          <>
            Current dashboard setup will be lost and replaced by the selected template. Do you wish
            to proceed?
          </>
        }
        header="Replace Dashboard Template"
        icon="pi pi-exclamation-triangle"
        handleContinue={handleConfirmTemplate}
        handleCancel={handleCancelTemplate}
      />
      <div className={`dashboard-tab${loading ? " fullheight" : ""}`}>
        {maximizedItem ? (
          <MaximizedView
            data={maximizedData}
            item={maximizedItem}
            onMinimize={handleMinimize}
            onSettings={handleSettings}
            onDeleteSuccess={() => handleDeleteSuccess(maximizedItem?.id)}
            onTitleSave={(newTitle) => handleTitleSave(maximizedItem.id, newTitle)}
          />
        ) : (
          <>
            <div className="dashboard-actions">
              <Form.Group className="mb-3" controlId="defaultSelect">
                <Form.Label>Apply Default</Form.Label>
                <Form.Select
                  value={selectedDefault}
                  onChange={handleDefaultChange}
                  className="view-dropdown"
                >
                  <option value="" disabled hidden>
                    Select default
                  </option>
                  <option value="Design Delivery">Design Delivery</option>
                  <option value="Flow Leader">Flow Leader</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3" controlId="viewSelect">
                <Form.Label>Add to view</Form.Label>
                <Form.Select
                  value={selectedView}
                  onChange={handleViewChange}
                  className="view-dropdown"
                >
                  <option value="" disabled hidden>
                    Select view
                  </option>
                  {!showSummaryView && isSummaryAccess && <option value="summary">Summary</option>}
                  <option value="project">Project</option>
                  <option value="task">Task</option>
                  <option value="fullkit">Fullkit</option>
                  <option value="jobs">Jobs</option>
                  <option value="alerts">Alert</option>
                  <option value="helpneeded">Help Needed</option>
                </Form.Select>
              </Form.Group>
              {viewItems?.length > ACCORDION_TOGGLE_MIN_ITEMS && (
                <Button
                  icon={allExpanded ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                  className="refresh-button"
                  tooltip={allExpanded ? "Close All" : "Open All"}
                  tooltipOptions={{ position: "bottom" }}
                  onClick={handleToggleAccordion}
                  disabled={loading}
                />
              )}
              {viewItems?.length > 0 && (
                <Button
                  icon="pi pi-refresh"
                  className="refresh-button"
                  tooltip="Refresh"
                  tooltipOptions={{ position: "bottom" }}
                  onClick={handleRefresh}
                  disabled={loading}
                />
              )}
            </div>
            <div className="view-container">
              {!loading && !viewItems?.length && (!viewItems || !showSummaryView) ? (
                <div className="empty-dashboard-help">
                  <div className="empty-dashboard-help-content">
                    <p className="empty-dashboard-help-title">No Dashboard modules found</p>
                    <p className="empty-dashboard-help-desc">
                      To get started, add a module by selecting an option from the{" "}
                      <span className="empty-dashboard-help-highlight">
                        &quot;Add to view&quot;
                      </span>{" "}
                      dropdown or apply a default template using the{" "}
                      <span className="empty-dashboard-help-highlight">
                        &quot;Apply Default&quot;
                      </span>{" "}
                      option above.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <DashboardColumn
                    items={leftColumnItems}
                    activeIndex={activeIndexLeft}
                    toast={toast}
                    handleMaximize={handleMaximize}
                    handleSettings={handleSettings}
                    handleRefresh={handleRefresh}
                    showSummary={showSummaryView}
                    summaryData={summaryModuleData}
                  />
                  <DashboardColumn
                    items={rightColumnItems}
                    activeIndex={activeIndexRight}
                    toast={toast}
                    handleMaximize={handleMaximize}
                    handleSettings={handleSettings}
                    handleRefresh={handleRefresh}
                  />
                </>
              )}
            </div>
          </>
        )}
      </div>
    </LoadingOverlay>
  );
};

export default DashboardTab;
